/**
 * Exposure = „diese Unit hat diese Variante gesehen". Nenner der Auswertung im
 * experimentEvaluator.
 *
 * Direkt ueber API.graphql statt ueber den mutation()-Wrapper: der verlangt
 * einen Username, den anonyme Funnel-Besucher nicht haben, und meldet jeden
 * Fehler an Bugsnag. Eine verlorene Exposure kostet ein Datum, keinen Nutzer,
 * daher nur eine Meldung pro Seitenaufruf.
 */
import { API } from 'aws-amplify'

import TRACK_EVENTS from '../../constants/track-events'
import { createExperimentExposure } from '../../graphql/mutations'
import { notifyBugsnag } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { buildContext, getFlags, resolveFlag } from './flags-store'
import { hasPersistentConsent, isVisitorIdFromLandingpage } from './visitor-id'

const seen = new Set()
let reportedFailure = false

const READ_BACK_DENIED =
  /^Not Authorized to access \w+ on type ExperimentExposure$/
export const isReadBackDenied = (message) => READ_BACK_DENIED.test(message)

const readRef = () => {
  try {
    const params = new URLSearchParams(window.location.search)
    return {
      utm_source: params.get('utm_source') || null,
      utm_campaign: params.get('utm_campaign') || null,
      has_gclid: params.has('gclid')
    }
  } catch {
    return { utm_source: null, utm_campaign: null, has_gclid: false }
  }
}

/**
 * @param {object} args
 * @param {object} args.flag
 * @param {string} args.variant
 * @param {object} args.ctx Kontext aus buildContext()
 * @param {string} [args.formId]
 */
export const trackExposure = ({ flag, variant, ctx, formId }) => {
  const unit = flag.unit || 'visitor'
  const unitId =
    unit === 'partner'
      ? ctx.partnerId
      : unit === 'user'
        ? ctx.userId
        : ctx.visitorId

  if (!unitId || !variant) return

  const dedupeKey = `${flag.key}:${unitId}`
  if (seen.has(dedupeKey)) return
  seen.add(dedupeKey)

  trackEvent(TRACK_EVENTS.EXPERIMENT_EXPOSED, {
    experiment_key: flag.key,
    variant,
    unit
  })

  // Jede Stage schreibt in ihr eigenes Backend, Testklicks auf develop oder
  // staging landen also nie in einer production-Auswertung.
  const input = {
    experiment_key: flag.key,
    variant,
    unit,
    unit_id: unitId,
    visitor_id: ctx.visitorId || null,
    surface: flag.surface || 'dashboard',
    form_id: formId || null,
    partner_id: ctx.partnerId || null,
    // Kommt die ID von der LP, hat die LP ueber die Speicherung entschieden.
    // Der Funnel im iframe sieht deren Consent nicht, also keine Aussage.
    consent: isVisitorIdFromLandingpage() ? null : hasPersistentConsent(),
    ...readRef()
  }

  API.graphql({
    query: createExperimentExposure,
    variables: { input },
    authMode: 'AWS_IAM'
  }).catch((err) => {
    // public IAM darf nur anlegen, nicht lesen. AppSync meldet dann je Feld
    // „Not Authorized to access <feld> on type ExperimentExposure“, obwohl die
    // Zeile geschrieben ist. Nur das ist harmlos. Ein „... on type Mutation“
    // heisst, die Zeile wurde NICHT geschrieben, und muss gemeldet werden.
    const messages = (err?.errors || []).map((e) => e?.message || '')
    if (messages.length && messages.every(isReadBackDenied)) return
    if (reportedFailure) return
    reportedFailure = true
    notifyBugsnag(
      new Error(
        `createExperimentExposure: ${err?.errors?.[0]?.message || err?.message || 'unknown'}`
      )
    )
  })
}

/**
 * Zuteilungen fuer alle laufenden Experimente einer Oberflaeche, eingefroren.
 * Fuer den Funnel: wer ihn oeffnet, hat seine Variante gesehen, egal welche
 * Komponente sie rendert. Nenner ist damit „Funnel geoeffnet“.
 *
 * Bewusst getrennt vom Schreiben: der Funnel friert beim Mount ein, die
 * Exposure geht erst nach createForm raus. Wuerde sie dann neu aufgeloest,
 * koennten inzwischen nachgeladene Flags eine Variante melden, die der
 * Besucher nie gesehen hat.
 *
 * @returns {Array<{flag: object, variant: string, ctx: object}>}
 */
export const freezeSurface = (surface, ctx = buildContext()) =>
  Object.values(getFlags())
    .filter((flag) => flag.kind === 'experiment' && flag.surface === surface)
    .map((flag) => ({ flag, ...resolveFlag(flag.key, ctx), ctx }))
    .filter((entry) => entry.tracked)
    .map(({ flag, variant }) => ({ flag, variant, ctx }))

/** Schreibt die eingefrorenen Exposures, dedupliziert je Unit. */
export const exposeFrozen = (frozen, { formId } = {}) => {
  ;(frozen || []).forEach(({ flag, variant, ctx }) =>
    trackExposure({ flag, variant, ctx, formId })
  )
}

// Nur fuer Tests
export const resetExposuresForTests = () => {
  seen.clear()
  reportedFailure = false
}
